import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import Badge from "../components/Badge";
import StatusChip from "../components/StatusChip";
import HebrewLockup from "../components/HebrewLockup";
import ListRow from "../components/ListRow";
import EmptyState from "../components/EmptyState";
import Button from "../components/Button";
import IconButton from "../components/IconButton";
import ProgressBar from "../components/ProgressBar";
import { SearchIcon, PlayIcon, CheckIcon } from "../components/icons";
import { fmt } from "../player/MiniPlayer";
import { getCatalogCounts } from "../api/redesignApi";
import { deriveCategoryProgress, loadServerPositions } from "../api/categoryProgress";
import { paths } from "../routing/paths";

/*
 * Category page per `Category Page.dc.html` (handoff README §5, brief
 * §7.2.5) — ONE template, two renderings decided by the contents:
 *
 *   container — the category has category/series children. Navy hero
 *   (lockup tile, CATEGORY chip, description, honest counts line), a
 *   "Start here" recommended-series strip, then "In this category":
 *   ONE curated, ordered, MIXED-TYPE list in backend order — categories,
 *   series, and classes interleave, never grouped. Type per row: navy
 *   CATEGORY chip + navy left accent + folder tile + chevron; blue
 *   SERIES chip + blue accent + Hebrew-lockup tile + chevron; class
 *   rows (ListRow) with media icon + play button, no accent.
 *
 *   list — a leaf of loose classes. Same hero (CATEGORY · CLASSES chip),
 *   sort chips (Default order / Newest / Oldest / Length), class rows
 *   with NEW badges, "Show N more classes".
 *
 * Length sort and media icons need the backend's ?ordered=1 payload
 * fields (duration/media); until that deploys the chip renders disabled
 * with a tooltip — the same graceful pattern as search. Reached only via
 * PermalinkPage, which owns loading and the designed 404.
 */

const NEW_WINDOW_DAYS = 30;
const LIST_PAGE_SIZE = 25;

const SORTS = [
  { key: "default", label: "Default order" },
  { key: "new", label: "Newest" },
  { key: "old", label: "Oldest" },
  { key: "len", label: "Length" },
];

function sortClasses(children, sort) {
  const copy = [...children];
  const time = (c) => (c.dateISO ? new Date(c.dateISO).getTime() : 0);
  if (sort === "new") copy.sort((a, b) => time(b) - time(a) || a.order - b.order);
  else if (sort === "old") copy.sort((a, b) => (time(a) || Infinity) - (time(b) || Infinity) || a.order - b.order);
  else if (sort === "len")
    copy.sort((a, b) => (a.durationSeconds ?? Infinity) - (b.durationSeconds ?? Infinity) || a.order - b.order);
  else copy.sort((a, b) => a.order - b.order);
  return copy;
}

function prettifySegment(segment) {
  return segment
    .replace(/^\d+-/, "") // legacy slugs carry a numeric ID prefix (169-rosh-hashana)
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function isNew(child) {
  if (!child.dateISO) return false;
  const age = Date.now() - new Date(child.dateISO).getTime();
  return age >= 0 && age < NEW_WINDOW_DAYS * 24 * 3600 * 1000;
}

function mediaLabel(media) {
  if (media === "video") return "Video";
  if (media === "audio") return "Audio";
  return null;
}

/**
 * Per-class progress (tier 1 everywhere, tier 2 hero for courses).
 * Server rows when signed in (cross-device), device markers otherwise.
 */
function useCategoryProgress(classes) {
  const [serverRows, setServerRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadServerPositions().then((rows) => !cancelled && rows && setServerRows(rows));
    return () => {
      cancelled = true;
    };
  }, [classes]);
  return useMemo(() => deriveCategoryProgress(classes, serverRows), [classes, serverRows]);
}

/** Deep counts for category/series children, fetched progressively. */
function useChildCounts(collections) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    // Cap the fan-out; /catalog/counts is 6h-cached server-side, so repeat
    // visits are free, but a cold 40-child category shouldn't fire 40 walks.
    const targets = collections.slice(0, 16);
    targets.forEach((child) => {
      const node = String(child.slug).split("/").filter(Boolean).pop();
      getCatalogCounts(node).then((c) => {
        if (!cancelled && c) setCounts((prev) => ({ ...prev, [child.id]: c }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [collections]);
  return counts;
}

function childCountLabel(c) {
  if (!c) return null;
  const parts = [];
  if (c.seriesCount > 0) parts.push(`${c.seriesCount} series`);
  if (c.classCount > 0) parts.push(`${c.classCount.toLocaleString()} ${c.classCount === 1 ? "class" : "classes"}`);
  return parts.join(" · ") || null;
}

const chevron = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#186CCE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/* Mixed-list rows — container mode. Left accent + chip mark the type. */

function CategoryRow({ child, counts }) {
  return (
    <Link
      to={paths.permalink(child.slug)}
      className="flex items-center gap-3 py-3.5 px-4 border-b border-ic-lightGray border-l-4 border-l-ic-navy no-underline hover:no-underline transition-colors duration-150 ease-out hover:bg-ic-offwhite focus-visible:outline-none focus-visible:shadow-[inset_0px_0px_0px_3px_rgba(24,108,206,0.1)]"
    >
      <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-ic-navy inline-flex items-center justify-center">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="navy" size="sm">CATEGORY</Badge>
          <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{child.title}</span>
        </div>
        <span className="text-[14px] leading-5 text-ic-slate">{childCountLabel(counts) || "Browse this collection"}</span>
      </div>
      {chevron}
    </Link>
  );
}

function SeriesRow({ child, counts }) {
  return (
    <Link
      to={paths.series(child.slug)}
      className="flex items-center gap-3 py-3.5 px-4 border-b border-ic-lightGray border-l-4 border-l-ic-blue no-underline hover:no-underline transition-colors duration-150 ease-out hover:bg-ic-offwhite focus-visible:outline-none focus-visible:shadow-[inset_0px_0px_0px_3px_rgba(24,108,206,0.1)]"
    >
      <span className="flex-shrink-0 w-11 h-11 rounded-xl overflow-hidden">
        <HebrewLockup label={child.title.split(/\s+/)[0]} className="bg-ic-navyCard" />
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="primary" size="sm">SERIES</Badge>
          <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{child.title}</span>
        </div>
        <span className="text-[14px] leading-5 text-ic-slate">{childCountLabel(counts) || "Every class, in order"}</span>
      </div>
      {chevron}
    </Link>
  );
}

/* ---------------- Page ---------------- */

export default function CategoryPage({ category }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sort, setSort] = useState("default");
  const [deepCounts, setDeepCounts] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const children = category.children;
  const collections = useMemo(() => children.filter((c) => c.type !== "class"), [children]);
  const classes = useMemo(() => children.filter((c) => c.type === "class"), [children]);
  const subCategories = collections.filter((c) => c.type === "category");
  const series = collections.filter((c) => c.type === "series");

  // ONE template, two renderings — decided by the category's contents.
  const mode = collections.length > 0 ? "container" : "list";
  // Tier 2: editorially-flagged leaf = full course treatment.
  const isCourse = mode === "list" && Boolean(category.displayAsCourse);

  const childCounts = useChildCounts(collections);
  const progress = useCategoryProgress(classes);

  useEffect(() => {
    getCatalogCounts(category.slug).then(setDeepCounts);
  }, [category.slug]);

  useEffect(() => {
    setSort("default");
    setShowAll(false);
  }, [category.id]);

  const crumbs = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    return segments.slice(0, -1).map((seg, i) => ({
      label: prettifySegment(seg),
      href: paths.permalink(segments.slice(0, i + 1).join("/")),
    }));
  }, [location.pathname]);

  // Honest counts line (container): "3 sub-categories · 3 series · 2
  // individual classes · 1,240 classes total". List: "217 classes".
  const knownMedia = [...new Set(classes.map((c) => c.media).filter(Boolean))];
  const mediaMix = knownMedia.length === 2 ? "Audio & video" : knownMedia.length === 1 ? mediaLabel(knownMedia[0]) : null;
  const countsLine = (mode === "container"
    ? [
        subCategories.length > 0 ? `${subCategories.length} sub-${subCategories.length === 1 ? "category" : "categories"}` : null,
        series.length > 0 ? `${series.length} series` : null,
        classes.length > 0 ? `${classes.length} individual ${classes.length === 1 ? "class" : "classes"}` : null,
        deepCounts?.classCount > 0 ? `${deepCounts.classCount.toLocaleString()} classes total` : null,
        mediaMix,
      ]
    : [
        `${(deepCounts?.classCount || classes.length).toLocaleString()} ${
          (deepCounts?.classCount || classes.length) === 1 ? "class" : "classes"
        }`,
        mediaMix,
      ]
  )
    .filter(Boolean)
    .join(" · ");

  // "Start here" — the first series in the curated order (no backend
  // "recommended" field yet; curated order is the editorial signal).
  const startHere = mode === "container" ? series[0] : null;
  const startHereCounts = startHere ? childCounts[startHere.id] : null;

  const sortedClasses = useMemo(() => sortClasses(classes, sort), [classes, sort]);
  const visibleClasses = mode === "list" && !showAll ? sortedClasses.slice(0, LIST_PAGE_SIZE) : sortedClasses;
  const hiddenCount = sortedClasses.length - visibleClasses.length;
  const lengthAvailable = classes.some((c) => c.durationSeconds != null);

  const classicHref = `${location.pathname}?view=classic`;

  const classRowMeta = (c) => {
    const parts = [
      c.durationSeconds != null ? fmt(c.durationSeconds) : null,
      mediaLabel(c.media),
      mode === "container" ? "One-off class" : c.dateLabel || null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : c.dateLabel || null;
  };

  // Tier 1 — light progress on every row: green check when finished, %
  // played (or "Started" without a duration) when partway through.
  const progressChip = (c) => {
    const p = progress.byId[String(c.id)];
    if (!p) return null;
    if (p.completed)
      return (
        <StatusChip kind="completed" size="sm">
          Completed
        </StatusChip>
      );
    return (
      <StatusChip kind="progress" size="sm">
        {p.pct != null ? `${p.pct}% played` : "Started"}
      </StatusChip>
    );
  };

  const classRow = (c, i, last) => (
    <ListRow
      key={c.id}
      mediaType={c.media || "audio"}
      chip={
        mode === "list" || progressChip(c) ? (
          <span className="inline-flex items-center gap-2">
            {mode === "list" && <Badge size="sm">{category.title}</Badge>}
            {isNew(c) && <StatusChip kind="new" size="sm">NEW</StatusChip>}
            {progressChip(c)}
          </span>
        ) : undefined
      }
      title={
        <Link to={paths.permalink(c.slug)} className="text-inherit no-underline hover:text-ic-blue hover:no-underline">
          {c.title}
        </Link>
      }
      meta={classRowMeta(c)}
      actionLabel={`Play ${c.title}`}
      onAction={() => navigate(paths.permalink(c.slug))}
      last={last}
      className="!py-3.5 !pl-5 !pr-4 !gap-3"
    />
  );

  const sortChip = (s) => {
    const disabled = s.key === "len" && !lengthAvailable;
    if (disabled)
      return (
        <span
          key={s.key}
          title="Length sorting arrives with class duration data"
          className="flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-slate/60 bg-white border border-ic-lightGray px-4 py-2.5 rounded-3xl min-h-[44px] inline-flex items-center cursor-not-allowed"
        >
          {s.label}
        </span>
      );
    const active = sort === s.key;
    return (
      <button
        key={s.key}
        type="button"
        aria-pressed={active}
        onClick={() => setSort(s.key)}
        className={
          active
            ? "flex-shrink-0 font-sans text-[14px] font-bold leading-5 text-white bg-ic-blue border border-ic-blue px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] focus-visible:outline-none focus-visible:shadow-ic-focus"
            : "flex-shrink-0 font-sans text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray px-4 py-2.5 rounded-3xl cursor-pointer min-h-[44px] transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
        }
      >
        {s.label}
      </button>
    );
  };

  return (
    <PublicLayout>
      <main
        className={`${mode === "container" ? "max-w-[1280px]" : "max-w-[960px]"} w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-6`}
      >
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="ic-rail flex items-center gap-1 overflow-x-auto text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          {crumbs.map((c) => (
            <React.Fragment key={c.href}>
              <span className="text-ic-slate">→</span>
              <Link to={c.href} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
                {c.label}
              </Link>
            </React.Fragment>
          ))}
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1 ic-wrap-anywhere">{category.title}</span>
        </nav>

        {/* Category hero — navy, both modes */}
        <section
          className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex gap-5 items-start flex-wrap"
          style={{ padding: "clamp(20px, 5vw, 32px)" }}
        >
          <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden hidden xs:block">
            <HebrewLockup hebrew={category.hebrewLabel} label={category.title} className="bg-ic-navyCard" hebrewSize={30} />
          </div>
          <div className="flex-1 min-w-[240px] flex flex-col gap-2">
            <span className="self-start">
              <Badge variant="onNavy">{mode === "container" ? "CATEGORY" : "CATEGORY · CLASSES"}</Badge>
            </span>
            <h1 className="m-0 font-bold text-white tracking-[-0.4px] ic-wrap-anywhere" style={{ fontSize: "clamp(26px, 6vw, 36px)", lineHeight: 1.25 }}>
              {category.title}
            </h1>
            {category.description && (
              <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[640px]">{category.description}</p>
            )}
            {countsLine && <span className="text-[14px] leading-5 text-ic-lightGray">{countsLine}</span>}

            {/* Course treatment (tier 2): progress + Resume, series-hero grammar */}
            {isCourse && classes.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-white/10 pt-4 mt-2 w-full">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <ProgressBar
                      value={classes.length > 0 ? Math.round((progress.completedCount / classes.length) * 100) : 0}
                      theme="dark"
                      height={6}
                    />
                  </div>
                  <span className="text-[14px] font-semibold leading-5 text-ic-offwhite flex-shrink-0">
                    {progress.completedCount} of {classes.length} completed
                  </span>
                </div>
                <div className="flex gap-3 flex-wrap items-center">
                  {progress.resume ? (
                    <>
                      <button
                        type="button"
                        onClick={() => navigate(paths.permalink(progress.resume.slug))}
                        className="bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold leading-7 px-8 h-12 border-0 rounded-3xl cursor-pointer inline-flex items-center gap-2 transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                      >
                        <PlayIcon size={18} /> Resume — Class {classes.findIndex((c) => String(c.id) === progress.currentId) + 1} of {classes.length}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(paths.permalink(classes[0].slug))}
                        className="bg-transparent text-ic-offwhite font-sans text-[16px] font-semibold leading-6 px-6 h-12 border border-ic-lightGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                      >
                        Start from the beginning
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(paths.permalink(classes[0].slug))}
                      className="bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold leading-7 px-8 h-12 border-0 rounded-3xl cursor-pointer inline-flex items-center gap-2 transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                    >
                      <PlayIcon size={18} /> Start from the beginning
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ============ container rendering ============ */}
        {mode === "container" && (
          <>
            {startHere && (
              <section className="flex flex-col gap-3">
                <span className="text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-slate">Start here</span>
                <Link
                  to={paths.series(startHere.slug)}
                  aria-label={`Start here: ${startHere.title}`}
                  className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised px-5 py-4 flex items-center gap-4 no-underline hover:no-underline transition-all duration-150 ease-out hover:shadow-ic-cta-hover hover:border-ic-borderGray focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                >
                  <span className="flex-shrink-0 w-12 h-12 rounded-full bg-ic-blue inline-flex items-center justify-center text-ic-offwhite">
                    <PlayIcon size={20} />
                  </span>
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{startHere.title}</span>
                    <span className="text-[14px] leading-5 text-ic-slate">
                      {["The recommended first series", childCountLabel(startHereCounts)].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  {chevron}
                </Link>
              </section>
            )}

            <section className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
                  In this category
                </h2>
                <Link
                  to={classicHref}
                  className="text-[14px] leading-5 flex-shrink-0 py-3 px-2 -my-3 -mx-2 inline-flex items-center min-h-[44px] box-border text-ic-blue font-semibold hover:underline"
                >
                  Classic view
                </Link>
              </div>
              {/* ONE curated, ordered, mixed-type list — backend order, never grouped */}
              <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
                {children.map((child, i) => {
                  const last = i === children.length - 1;
                  if (child.type === "category") return <CategoryRow key={`c${child.id}`} child={child} counts={childCounts[child.id]} />;
                  if (child.type === "series") return <SeriesRow key={`s${child.id}`} child={child} counts={childCounts[child.id]} />;
                  return classRow(child, i, last);
                })}
              </div>
            </section>
          </>
        )}

        {/* ============ list rendering ============ */}
        {mode === "list" && !isCourse && classes.length > 0 && (
          <>
            {/* Tier 1 — continue where you left off, pinned above the list */}
            {progress.resume && (
              <section className="flex flex-col gap-3">
                <span className="text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-slate">Continue</span>
                <Link
                  to={paths.permalink(progress.resume.slug)}
                  aria-label={`Continue ${progress.resume.title}`}
                  className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised px-5 py-4 flex items-center gap-4 no-underline hover:no-underline transition-all duration-150 ease-out hover:shadow-ic-cta-hover hover:border-ic-borderGray focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                >
                  <span className="flex-shrink-0 w-12 h-12 rounded-full bg-ic-blue inline-flex items-center justify-center text-ic-offwhite">
                    <PlayIcon size={20} />
                  </span>
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{progress.resume.title}</span>
                    <span className="text-[14px] leading-5 text-ic-slate">
                      {(() => {
                        const p = progress.byId[String(progress.resume.id)];
                        return p?.duration > 0
                          ? `Continue where you left off · ${fmt(p.position)} of ${fmt(p.duration)}`
                          : "Continue where you left off";
                      })()}
                    </span>
                  </div>
                  {chevron}
                </Link>
              </section>
            )}
            <div className="ic-rail flex gap-2 overflow-x-auto items-center">
              <span className="flex-shrink-0 text-[12px] font-bold tracking-[0.5px] uppercase text-ic-slate">Sort</span>
              {SORTS.map(sortChip)}
            </div>
            <section className="flex flex-col gap-3">
              <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
                {visibleClasses.map((c, i) => classRow(c, i, i === visibleClasses.length - 1 && hiddenCount === 0))}
              </div>
              {hiddenCount > 0 && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="bg-transparent text-ic-body font-sans text-[16px] font-semibold leading-6 px-6 py-2.5 min-h-[48px] border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue active:bg-black/5 focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                  >
                    Show {hiddenCount} more {hiddenCount === 1 ? "class" : "classes"}
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {/* ============ course rendering (tier 2) ============ */}
        {isCourse && classes.length > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
              Course outline
            </h2>
            {/* Curated order is the course order — no sort chips here; rows
                use the series-outline grammar: done ✓ / current UP NEXT /
                future numbered. */}
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
              {(showAll || classes.length <= 12 ? classes : classes.slice(0, 10)).map((c, idx, arr) => {
                const p = progress.byId[String(c.id)];
                const state = String(c.id) === progress.currentId ? "current" : p ? "done" : "future";
                const last = idx === arr.length - 1 && arr.length === classes.length;
                if (state === "current") {
                  return (
                    <div key={c.id} className={`flex items-center gap-3 p-4 ${last ? "" : "border-b border-ic-lightGray"} bg-[rgba(24,108,206,0.05)] border-l-4 border-l-ic-blue`}>
                      <span aria-label="Up next" className="flex-shrink-0 w-11 h-11 rounded-full bg-ic-blue inline-flex items-center justify-center text-ic-offwhite text-[14px] font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={paths.permalink(c.slug)} className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                            {c.title}
                          </Link>
                          <span className="bg-ic-blue text-white text-[11px] font-bold tracking-[0.5px] px-2.5 py-0.5 rounded-xl flex-shrink-0">UP NEXT</span>
                        </div>
                        <span className="text-[14px] leading-5 text-ic-slate">
                          {[p?.pct != null ? `${p.pct}% played` : null, classRowMeta(c)].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                      <IconButton variant="solid" size={48} label="Resume this class" onClick={() => navigate(paths.permalink(c.slug))}>
                        <PlayIcon size={20} />
                      </IconButton>
                    </div>
                  );
                }
                if (state === "done") {
                  return (
                    <div key={c.id} className={`flex items-center gap-3 px-4 py-3 ${last ? "" : "border-b border-ic-lightGray"} transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
                      <span aria-label={p.completed ? "Completed" : "Started"} className="flex-shrink-0 w-11 h-11 rounded-full bg-[rgba(36,154,26,0.1)] inline-flex items-center justify-center text-ic-success">
                        <CheckIcon size={18} strokeWidth={2} />
                      </span>
                      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <Link to={paths.permalink(c.slug)} className="text-[18px] font-semibold leading-7 text-ic-slate no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                          {idx + 1}. {c.title}
                        </Link>
                        <span className="text-[14px] leading-5 text-ic-slate">
                          {[p.completed ? "Completed" : `${p.pct != null ? `${p.pct}% played` : "Started"}`, c.dateLabel].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                      <IconButton variant="circleOutline" size={44} label="Play again" onClick={() => navigate(paths.permalink(c.slug))}>
                        <PlayIcon size={16} />
                      </IconButton>
                    </div>
                  );
                }
                return (
                  <div key={c.id} className={`flex items-center gap-3 px-4 py-3 ${last ? "" : "border-b border-ic-lightGray"} transition-colors duration-150 ease-out hover:bg-ic-offwhite`}>
                    <span className="flex-shrink-0 w-11 h-11 rounded-full bg-ic-offwhite border border-ic-lightGray inline-flex items-center justify-center text-ic-slate text-[14px] font-bold">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                      <Link to={paths.permalink(c.slug)} className="text-[18px] font-semibold leading-7 text-black no-underline hover:text-ic-blue hover:no-underline ic-wrap-anywhere">
                        {c.title}
                      </Link>
                      {classRowMeta(c) && <span className="text-[14px] leading-5 text-ic-slate">{classRowMeta(c)}</span>}
                    </div>
                    <IconButton variant="circleOutline" size={44} label="Play this class" onClick={() => navigate(paths.permalink(c.slug))}>
                      <PlayIcon size={16} />
                    </IconButton>
                  </div>
                );
              })}
              {!showAll && classes.length > 12 && (
                <div className="flex justify-center p-4">
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="bg-transparent text-ic-body font-sans text-[16px] font-semibold leading-6 px-6 py-2.5 min-h-[48px] border border-ic-borderGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue active:bg-black/5 focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                  >
                    Show all {classes.length} classes
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Designed empty state — never blank */}
        {children.length === 0 && (
          <EmptyState
            icon={<SearchIcon size={28} />}
            title="Nothing filed here yet"
            body="This category is empty for now — but with 15,000+ classes in the archive, what you're looking for almost certainly exists."
            actions={
              <Link to={paths.search()}>
                <Button size="lg">Search the archive</Button>
              </Link>
            }
          />
        )}
      </main>
    </PublicLayout>
  );
}
